import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { useTranslator } from "@eo-locale/react";

import { Center, Spinner, Stack } from "@/storybook-components";
import Alerts from "@/components/alerts";
import UnverifiedUserForm from "@/components/unverified-user/form";
import useAuthGate from "@/hooks/useAuthGate";
import useEmbeds from "@/hooks/useEmbeds";
import useUser from "@/hooks/useUser";
import { clearAllErrors, resetAnswers } from "@/redux/features/questions/slice";
import { fetchQuickPoll } from "@/redux/features/quick-poll/thunk";
import {
  clearTaken,
  markTaken,
  showForm
} from "@/redux/features/quick-poll/slice";
import { resetUnverifiedUser } from "@/redux/features/user/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import type { QuickPollScope } from "@/types";

import Form from "./form";
import { GuestLoginGate, RegisteredLoginGate } from "./gates";
import PollHeading from "./heading";
import {
  clearAllPollVotesLocally,
  getAlert,
  getAuthRoutes,
  getParticipation,
  getPollVoteSessionOwner,
  getPostAuthReturnPath,
  getStoredPollVote,
  isAnonymousVoteOwner,
  isPollFormDisabled,
  isUnverifiedVoter,
  pollIdsMatch,
  usesAnonymousVoteMemory,
  type PollVoteSessionOwner
} from "./logic";
import Response from "./response";

const Content = (scope: QuickPollScope): JSX.Element => {
  const { translate } = useTranslator();
  const asPath = usePathname() ?? "/";
  const dispatch = useAppDispatch();
  const { quickPoll, error, taken, editingResponse, viewResult } =
    useAppSelector((state) => state.quickPoll);
  const { disableSignup } = useAppSelector((state) => state.settings.settings);
  const { profile, isLoggedIn } = useAppSelector(
    (state) => state.user.registeredUser
  );
  const project = useAppSelector((state) => state.project?.project);
  const projectPermalink = project?.permalink;
  const userType = useAppSelector((state) => state.user.type);
  const { isRegisteredUserLoggedIn, isGuestUser, isUnverfiedUserLoggedIn } =
    useUser();
  const {
    isGuestParticipationEnabled,
    requireProfileCompletion,
    openGuestLoginModal
  } = useAuthGate();
  const embed = useEmbeds();

  const { projectId, quickPollId } = scope;
  const voteOwner = getPollVoteSessionOwner({
    isRegisteredUserLoggedIn: isRegisteredUserLoggedIn || !!isLoggedIn,
    userId: profile?.id,
    userType
  });
  const isAnonymous = isAnonymousVoteOwner(voteOwner);
  const anonymousVoteMemory = usesAnonymousVoteMemory(
    isAnonymous,
    quickPoll.allowUnverifiedParticipation,
    isGuestParticipationEnabled
  );
  const previousVoteOwnerRef = useRef<PollVoteSessionOwner | null>(null);
  const isWidget = scope.variant === "widget";
  const isCurrentPoll = pollIdsMatch(
    quickPoll.id,
    quickPollId,
    quickPoll.permalink
  );
  const pollLoaded = (isCurrentPoll && !!quickPoll.name) || !!error;
  // Gate form/results until anonymous localStorage has been read (session users
  // trust the API and are ready as soon as the poll is loaded).
  const [voteChecked, setVoteChecked] = useState(
    !anonymousVoteMemory && pollLoaded
  );

  const userHasTakenPoll = anonymousVoteMemory
    ? taken || !!quickPoll.takenByCurrentUser
    : !!quickPoll.takenByCurrentUser;
  const showResultsView = (viewResult || userHasTakenPoll) && !editingResponse;

  useEffect(() => {
    if (!isWidget || !isCurrentPoll || userHasTakenPoll || viewResult) return;
    dispatch(showForm());
  }, [
    dispatch,
    isWidget,
    isCurrentPoll,
    quickPollId,
    userHasTakenPoll,
    viewResult
  ]);

  // Client navigation between polls — SSR already loaded the first poll.
  // Do not reset on unmount: React Strict Mode remounts in dev and would wipe
  // hydrated state without re-fetching (deps unchanged → perpetual spinner).
  useEffect(() => {
    if (!projectId || !quickPollId || isCurrentPoll) return;

    setVoteChecked(false);
    dispatch(fetchQuickPoll({ projectId, quickPollId }));
  }, [dispatch, isCurrentPoll, projectId, quickPollId]);

  // Keep vote localStorage tied to the current auth session without touching
  // global login/logout handlers: clear when the session owner changes.
  useEffect(() => {
    const previousOwner = previousVoteOwnerRef.current;
    previousVoteOwnerRef.current = voteOwner;

    if (previousOwner == null || previousOwner === voteOwner) return;

    clearAllPollVotesLocally();
    dispatch(clearTaken());
    setVoteChecked(false);
  }, [dispatch, voteOwner]);

  // Apply localStorage vote for anonymous users after poll data is present.
  // Session users skip this — takenByCurrentUser / userOptionId come from the API.
  useEffect(() => {
    if (!pollLoaded || !projectId || !quickPollId) {
      setVoteChecked(false);
      return;
    }

    if (!anonymousVoteMemory) {
      setVoteChecked(true);
      return;
    }

    const stored = getStoredPollVote(projectId, quickPollId);
    if (stored) {
      dispatch(markTaken({ optionId: stored.optionId }));
    } else if (taken && !quickPoll.takenByCurrentUser) {
      dispatch(clearTaken());
    }

    setVoteChecked(true);
  }, [
    dispatch,
    projectId,
    quickPollId,
    pollLoaded,
    quickPoll.takenByCurrentUser,
    taken,
    anonymousVoteMemory
  ]);

  const participation = getParticipation({
    quickPoll,
    isRegisteredUserLoggedIn,
    isGuestParticipationEnabled,
    isUnverifiedUserLoggedIn: isUnverfiedUserLoggedIn
  });
  const formDisabled = isPollFormDisabled({
    gate: participation.gate,
    visibilityMode: project?.visibilityMode,
    canContribute: project?.canContribute
  });
  const panelRestricted =
    project?.visibilityMode === "Panelled" && project?.canContribute === false;

  const guestNeedsProfileForRegisteredPoll =
    isGuestUser &&
    isGuestParticipationEnabled &&
    participation.allowOnlyRegisteredParticipation;

  const projectReturnPath = projectPermalink || projectId;
  const pollReturnId = String(quickPoll.id || quickPollId || "");
  const authReturnOptions = {
    projectId: projectReturnPath,
    quickPollId: pollReturnId || undefined,
    variant: scope.variant
  };
  const postAuthReturnPath = getPostAuthReturnPath(
    asPath,
    embed,
    authReturnOptions
  );

  useEffect(() => {
    if (guestNeedsProfileForRegisteredPoll) {
      requireProfileCompletion({
        reason: "profileGate.requiredForQuickPoll",
        source: "quick-poll",
        returnTo: postAuthReturnPath
      });
    }
  }, [
    guestNeedsProfileForRegisteredPoll,
    requireProfileCompletion,
    postAuthReturnPath
  ]);

  const authRoutes = getAuthRoutes(asPath, embed, authReturnOptions);
  const alert = getAlert(quickPoll, error, translate);
  const isLoading = !pollLoaded || !voteChecked;
  const handleGuestLogin = () => {
    // Let the click finish so Zag does not treat it as interact-outside
    // while the dialog content is still attaching (iframe timeout).
    window.setTimeout(() => {
      openGuestLoginModal({ initialStep: "email-login" });
    }, 0);
  };

  const heading = <PollHeading name={quickPoll.name} variant={scope.variant} />;

  const renderBody = (): JSX.Element => {
    if (isLoading) {
      return (
        <Center py={isWidget ? 4 : 8}>
          <Spinner size={isWidget ? "md" : "lg"} />
        </Center>
      );
    }

    if (alert) {
      return <Alerts status={alert.status} title={alert.message} />;
    }

    if (
      participation.gate === "unverified-user" ||
      participation.gate === "guest-login"
    ) {
      return (
        <>
          {heading}
          {participation.gate === "guest-login" ? (
            <GuestLoginGate
              login={authRoutes.login}
              target={authRoutes.target}
              onContinueWithEmail={handleGuestLogin}
            />
          ) : (
            <UnverifiedUserForm tool={translate("quickPoll.quickPoll")} />
          )}
        </>
      );
    }

    return (
      <>
        {heading}
        {participation.gate === "registered-login" ? (
          <RegisteredLoginGate {...authRoutes} disableSignup={disableSignup} />
        ) : null}
        {showResultsView ? (
          <Response
            taken={userHasTakenPoll}
            onGoToForm={() => {
              if (
                userHasTakenPoll &&
                isUnverifiedVoter(
                  isUnverfiedUserLoggedIn,
                  isRegisteredUserLoggedIn
                )
              ) {
                clearAllPollVotesLocally();
                dispatch(resetUnverifiedUser());
                dispatch(resetAnswers());
                dispatch(clearAllErrors());
                dispatch(clearTaken());
              }

              dispatch(showForm());
            }}
          />
        ) : (
          <>
            {panelRestricted ? (
              <Alerts
                status="info"
                description={translate("common.projectPanelledAlert")}
                data-testid="quick-poll-panel-alert"
              />
            ) : null}
            <Form
              projectId={projectId}
              quickPollId={quickPollId}
              disabled={formDisabled}
            />
          </>
        )}
      </>
    );
  };

  const isEmbedMode = embed.isEmbed && !isWidget;

  return (
    <Stack
      gap={4}
      className="ehq-quickPollContainer"
      width="100%"
      p={isEmbedMode ? 4 : undefined}
    >
      {renderBody()}
    </Stack>
  );
};

export default Content;
